import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Container, Form, Button, Card, InputGroup, Spinner } from 'react-bootstrap';
import { FaUser } from 'react-icons/fa';
import { MdOutlineEmail } from "react-icons/md";
import { IoEyeOutline } from "react-icons/io5";
import { login } from '../utils/api';
import '../styles/Login.css';
import fttsLogo from '../images/logo.png';

function Login({ onLoginSuccess }) {
  const [credentials, setCredentials] = useState({
    email: '',
    password: ''
  });
  const [errors, setErrors] = useState({
    email: '',
    password: '',
    general: ''
  });
  const [rememberMe, setRememberMe] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const navigate = useNavigate();

  const handleChange = (e) => {
    const { name, value } = e.target;
    setCredentials({
      ...credentials,
      [name]: value
    });
    
    // Clear error when user starts typing
    if (errors[name]) {
      setErrors({
        ...errors,
        [name]: ''
      });
    }
  };

  const validateForm = () => {
    let isValid = true;
    const newErrors = { email: '', password: '', general: '' };
    
    if (!credentials.email) {
      newErrors.email = 'Please enter your valid email address.';
      isValid = false;
    } else if (!/\S+@\S+\.\S+/.test(credentials.email)) {
      newErrors.email = 'Please enter a valid email address.';
      isValid = false;
    }
    
    if (!credentials.password) {
      newErrors.password = 'Please enter your valid password.';
      isValid = false;
    }
    
    setErrors(newErrors);
    return isValid;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (validateForm()) {
      setIsLoading(true);
      try {
        const response = await login(credentials.email, credentials.password);
        if (response.success) {
          // Call the onLoginSuccess callback to update state in App.js
          if (onLoginSuccess) {
            onLoginSuccess();
          }
          // Redirect to home page
          navigate('/');
        } else {
          setErrors({
            ...errors,
            general: response.error || 'Login failed. Please try again.'
          });
        }
      } catch (error) {
        setErrors({
          ...errors,
          general: error.message || 'Login failed. Please check your credentials.'
        });
      } finally {
        setIsLoading(false);
      }
    }
  };

  return (
    <div className="login-wrapper">
      <Container className="login-container">
        <Card className="login-card">
          <div className="logo-container">
            <div className="circle-logo">
              <img src={fttsLogo} alt="FTTS Logo" className="logo-image" />
            </div>
          </div>
          
          <div className="login-content">
            <h3 className="welcome-text">Welcome Back!</h3>
            <p className="subtitle">10 Years of Excellence in Reliable GPS Tracking</p>
            <p className="tagline">"Innovative. Reliable. Complete GPS Solutions."</p>

            {errors.general && <div className="error-message">{errors.general}</div>}

            <Form onSubmit={handleSubmit}>
              <Form.Group className="mb-3">
                <Form.Label>Email Address</Form.Label>
                <InputGroup className={errors.email ? 'has-error' : ''}>
                  <Form.Control 
                    type="email" 
                    name="email"
                    value={credentials.email}
                    onChange={handleChange}
                    placeholder="Enter your email..." 
                    isInvalid={!!errors.email}
                  />
                  <InputGroup.Text>
                    <MdOutlineEmail />
                  </InputGroup.Text>
                </InputGroup>
                {errors.email && <div className="error-text">{errors.email}</div>}
              </Form.Group>

              <Form.Group className="mb-3">
                <Form.Label>Password</Form.Label>
                <InputGroup className={errors.password ? 'has-error' : ''}>
                  <Form.Control 
                    type="password" 
                    name="password"
                    value={credentials.password}
                    onChange={handleChange}
                    placeholder="Enter your password..." 
                    isInvalid={!!errors.password}
                  />
                  <InputGroup.Text>
                     <IoEyeOutline />
                  </InputGroup.Text>
                </InputGroup>
                {errors.password && <div className="error-text">{errors.password}</div>}
              </Form.Group>

              <Form.Group className="mb-3 remember-me">
                <Form.Check 
                  type="checkbox" 
                  label="Remember me" 
                  checked={rememberMe}
                  onChange={() => setRememberMe(!rememberMe)}
                />
              </Form.Group>

              <Button className="login-btn" variant="primary" type="submit" disabled={isLoading}>
                {isLoading ? (
                  <><Spinner animation="border" size="sm" className="me-2" /> Logging in...</>
                ) : (
                  <><FaUser className="me-2" /> Login</>
                )}
              </Button>
              <div className="mt-3 text-center">
                <small className="text-muted">Demo: admin@gps.com / password123</small>
              </div>
            </Form>
          </div>
        </Card>
      </Container>
      <div className="footer-text">
        © {new Date().getFullYear()} First Tech Tracking | Excellence in GPS Tracking | Contact Us
      </div>
    </div>
  );
}

export default Login;

